import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { ProductService } from "$services";

const IMPORT_SERVER_ERROR_MSG =
  "Une erreur est survenue lorsque vous avez importé votre fichier. Veuillez importer un nouveau fichier";
const IMPORT_CLIENT_ERROR_MSG_PANIER =
  "Le fichier que vous essayez d'importer contient un ou plusieurs champs obligatoires vides (indiqués par un astérisque *) ou une quantité inférieure à 1. Veuillez apporter les modifications requises et importer de nouveau le fichier";
const IMPORT_CLIENT_ERROR_MSG_REF404 =
  "Les références suivantes ne semblent pas exister dans le catalogue de cette entreprise :\r\n";
const IMPORT_CLIENT_ERROR_MSG_INACTIVE =
  "Les références suivantes sont inactives et ne peuvent pas être ajoutées :\r\n";

export default function useProductImport({
  addToCart,
  companyId,
}: {
  addToCart: any;
  companyId: string;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [errorImport, setErrorImport] = useState<any>("");
  const [dataImport, setDataImport] = useState<any>(undefined);
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);
  const [isImportLoading, setIsImportLoading] = useState<boolean>(false);
  const [isImportPopinOpen, setIsImportPopinOpen] = useState<boolean>(false);
  const [totalImportSuccess, setTotalImportSuccess] = useState<any>(undefined);

  const isImportFinished = totalImportSuccess !== undefined;

  const handleToggle = () => {
    setIsMenuOpened((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setIsMenuOpened(false);
  };

  const downloadTemplatePanier = () => {
    try {
      const ws = XLSX.utils.aoa_to_sheet([
        ["Référence dans le catalogue*", "Quantité(s)*"],
        ["001ref-exemple", 1],
      ]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      XLSX.writeFile(wb, "Template import produits edb.xlsx");
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const handleCLickOnImportFile = () => {
    setIsMenuOpened(false);
    setIsImportPopinOpen(true);
  };

  const handleCancelImportDialog = () => {
    setTotalImportSuccess(undefined);
    setIsImportPopinOpen(false);
    setErrorImport("");
  };

  const aggregateProductLines = (lines: any[]) => {
    const aggregatedMap = new Map();

    lines.forEach((line) => {
      const reference = line[0];
      const qty = parseInt(`${line[1]}`, 10);

      if (aggregatedMap.has(reference)) {
        aggregatedMap.set(reference, aggregatedMap.get(reference) + qty);
      } else {
        aggregatedMap.set(reference, qty);
      }
    });

    return Array.from(aggregatedMap.entries()).map(([reference, qty]) => ({
      reference,
      qty,
    }));
  };

  const getAllProductsByReferences = async (lines) => {
    try {
      const aggregatedProductLines = aggregateProductLines(lines);

      const allProductsFound =
        await ProductService.getProductsByReferencesAndCompany(
          aggregatedProductLines.map((p) => p.reference),
          companyId,
          true
        );

      if (allProductsFound.length !== aggregatedProductLines.length) {
        let error = IMPORT_CLIENT_ERROR_MSG_REF404;
        const originalLinesMap = new Map();

        lines.forEach((l, i) => {
          if (!originalLinesMap.has(l[0])) {
            originalLinesMap.set(l[0], []);
          }
          originalLinesMap.get(l[0]).push(i + 2); // +2 because of header and 1-based indexing
        });

        aggregatedProductLines.forEach((pl) => {
          const found = allProductsFound.find(
            (p) => p.reference === pl.reference
          );
          if (!found) {
            const lineNumbers = originalLinesMap.get(pl.reference);
            if (lineNumbers && lineNumbers.length > 0) {
              if (lineNumbers.length === 1) {
                error += `- Ligne ${lineNumbers[0]} : "${pl.reference}"\r\n`;
              } else {
                error += `- Lignes ${lineNumbers.join(", ")} : "${
                  pl.reference
                }"\r\n`;
              }
            }
          }
        });

        setErrorImport(error);
        return false;
      }

      // Trouver les références inactives
      const inactiveReferences: string[] = [];
      allProductsFound.forEach((p) => {
        if (!p.isActive) {
          inactiveReferences.push(`- "${p.reference}"`);
        }
      });

      if (inactiveReferences.length > 0) {
        const error =
          IMPORT_CLIENT_ERROR_MSG_INACTIVE + inactiveReferences.join("\r\n");
        setErrorImport(error);
        return false;
      }

      const productsToAdd = allProductsFound.map((p) => {
        const aggregatedLine = aggregatedProductLines.find(
          (pl) => pl.reference === p.reference
        );
        const qty = aggregatedLine?.qty || 0;
        return {
          productID: p?._id,
          qty,
        };
      });

      return productsToAdd.filter((p: any) => !!p.productID && p.qty > 0);
    } catch (e) {
      return false;
    }
  };

  const handleValidateImportFile = async () => {
    if (errorImport.length > 0) {
      setErrorImport("");
      setDataImport(undefined);
      setIsImportPopinOpen(false);
      return;
    }
    if (isImportFinished) {
      setTotalImportSuccess(undefined);
      setIsImportPopinOpen(false);
      return;
    }
    try {
      if (!dataImport?.data) {
        setErrorImport(IMPORT_CLIENT_ERROR_MSG_PANIER);
        setDataImport(undefined);
      } else {
        setIsImportLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [header, ...lines]: any[] = dataImport.data;

        const filled = lines.every((line: any) => !!line[0] && !!line[1]);
        if (!filled) {
          setErrorImport(IMPORT_CLIENT_ERROR_MSG_PANIER);
          setDataImport(undefined);
        } else {
          const allProductsToAdd = await getAllProductsByReferences(lines);

          if (!allProductsToAdd || allProductsToAdd.length === 0) {
            setDataImport(undefined);
          } else {
            // handle add panier
            allProductsToAdd.forEach((d: any) => {
              addToCart(d.productID, d?.qty, false); // false on ajoute on ne remplace pas la valeur
            });
            setTotalImportSuccess(allProductsToAdd.length);
          }
        }
        setIsImportLoading(false);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      setErrorImport(IMPORT_SERVER_ERROR_MSG);
      setDataImport(undefined);
      setIsImportLoading(false);
    }
  };

  const handleImportSuccess = async (data: any[], acceptedCsv: any) => {
    setErrorImport("");
    setDataImport({ data, acceptedCsv });
  };

  const onError = () => {
    setErrorImport(IMPORT_CLIENT_ERROR_MSG_PANIER);
    setDataImport(undefined);
  };

  return {
    anchorRef,
    dataImport,
    errorImport,
    isMenuOpened,
    isImportLoading,
    isImportFinished,
    isImportPopinOpen,
    totalImportSuccess,

    setDataImport,
    setErrorImport,
    setIsMenuOpened,
    setIsImportPopinOpen,
    setTotalImportSuccess,

    onError,
    handleClose,
    handleToggle,
    handleImportSuccess,
    downloadTemplatePanier,
    handleCLickOnImportFile,
    handleCancelImportDialog,
    handleValidateImportFile,
  };
}
